import { Component, EventEmitter, forwardRef, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, debounceTime, filter, map, Observable, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { LocalizationService } from '../../services/localization.service';
import { DropdownInputsSignalService } from 'src/app/core/services/dropdown-inputs-signal.service';
import { WizardCoachOverlayUiService } from '../../services/wizard-coach-overlay-ui.service';

@Component({
  selector: 'app-select-input-row',
  templateUrl: './select-input-row.component.html',
  styleUrl: './select-input-row.component.scss',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SelectInputRowComponent),
    multi: true
  }]
})
export class SelectInputRowComponent implements OnDestroy, OnInit, OnChanges {
  @Input({ required: true }) bindLabel!: string | { [lang: string]: string };
  @Input({ required: true }) bindValue!: string;
  @Input({ required: true }) placeholder!: string;
  @Input({ required: false }) label?: string;
  @Input({ required: true }) errorMessage!: string;

  @Input({ required: false }) control?: FormControl;
  @Input({ required: true }) fetchFn!: (pageNumber: number, pageSize: number, term: string)
    => Observable<any>;
  @Input({ required: true }) fetchSelectedItem!: (id: any) => Observable<any>;

  @Input({ required: false }) width = "25%";
  /** When true, label is beside input; when false (default), label on top and input below. */
  @Input({ required: false }) labelBesideInput = false;
  @Input({ required: false }) id?: string;
  @Input({ required: false }) writeValueFlag = true;
  @Input({ required: false }) clearAfterSelection = false;
  @Input({ required: false }) isResponsive = true;
  @Input({ required: false }) isRequired = false;

  @Input() appendTo = '';

  /**
   * ng-select panel position. Use 'top' or 'bottom' to force; 'auto' uses default behavior
   * except during an active wizard coach overlay, where 'top' is applied so the panel
   * does not cover the coach mark below the field.
   */
  @Input() dropdownPosition: 'auto' | 'top' | 'bottom' = 'auto';

  @Input() refresh = 0;
  @Output() emitSelectOption = new EventEmitter<any>();

  @Input() filteroutIds: string[] = [];
  @Input() fileroutKey = 'id';

  @Input() multiple = false;

  /**
   * When false, `fetchSelectedItem` is not merged into the dropdown `items$` list (only
   * `fetchFn` results appear). Use for permission-scoped lists where GET-by-id must not
   * inject a row into the selectable options. Orphan form values may show empty until
   * the user picks from the list; `validateCurrentValueAgainstItems` is skipped for this mode.
   */
  @Input() mergeFetchedSelectedIntoItems = true;

  destory$ = new Subject();

  currentBindLabel: string;

  @Input() disabled = false;

  /** When true, ng-select shows loading spinner (e.g. initial currency load). */
  @Input() externalLoading = false;

  @ViewChild('selectRef') selectRef: any;

  readonly wizardCoachUi = inject(WizardCoachOverlayUiService);

  value: any;
  onChange = (value: any) => { };
  onTouched = () => { };

  get labelWidth(): string {
    return this.width || '25%';
  }

  items$ = new BehaviorSubject<any[]>([]);
  isLoading = false;

  private searchTerm$ = new BehaviorSubject<{ term: string; pageNumber: number }>({ term: '', pageNumber: 1 });

  currentPage = 1;
  pageSize = 10;
  hasMoreItems = true;

  itemFetched = false;

  customSearch?: string;

  clearGurad = true;

  isFirstTimeChange = true;

  ngOnChanges(changes: SimpleChanges) {
    if ((changes.refresh || changes.id) && !this.isFirstTimeChange) {
      this.onSearch('', 1);
    } else if (changes.refresh || changes.id) {
      this.isFirstTimeChange = false;
    }

    if (changes.filteroutIds) {
      this.items$.next(this.items$.getValue().filter((el: any) => !this.filteroutIds.includes(el[this.fileroutKey].toString() ?? '')))
    }

    if (
      changes['mergeFetchedSelectedIntoItems'] &&
      !changes['mergeFetchedSelectedIntoItems'].firstChange
    ) {
      this.itemFetched = false;
      const v = this.control?.value;
      if (v != null && v !== undefined) {
        queueMicrotask(() => this.writeValue(v));
      }
    }
  }

  constructor(
    private localaztionService: LocalizationService,
    private dropdownSignalService: DropdownInputsSignalService
  ) { }

  getLabel = () => {
    if (typeof this.bindLabel === 'string') {
      this.currentBindLabel = this.bindLabel;
      return
    }

    this.currentBindLabel =
      this.localaztionService.getCachedLanguage() === 'ar'
        ? this.bindLabel['ar'] ?? this.bindLabel['en']
        : this.bindLabel['en'] ?? this.bindLabel['ar'];
  }

  ngOnInit(): void {
    this.initializeData();

    this.setupSearchSubscription();

    this.dropdownSignalService.reload$
      .pipe(
        takeUntil(this.destory$),
        filter(reloadId => reloadId === this.id)
      )
      .subscribe(() => {
        this.onSearch('', 1);
      });

    // Subscribe to form control value changes to ensure writeValue is called
    // when value is set programmatically after component initialization
    if (this.control) {
      this.control.valueChanges
        .pipe(takeUntil(this.destory$))
        .subscribe(value => {
          // Reset itemFetched flag when value changes to allow re-fetching if needed
          if (value !== this.value) {
            this.itemFetched = false;
          }
          // Call writeValue for all values including null to ensure component state is synced
          this.writeValue(value);
        });
      // valueChanges لا يُصدر للقيمة الأولية — استدعاء writeValue إن وُجدت قيمة عند التهيئة
      if (this.control.value != null) {
        queueMicrotask(() => this.writeValue(this.control!.value));
      }
    }

    // if (this.id === 'product-test') {
    //   this.items$.subscribe(console.log);
    //   this.searchTerm$.subscribe(console.log);
    // }
  }

  private setupSearchSubscription() {
    this.searchTerm$
      .pipe(
        debounceTime(300),
        takeUntil(this.destory$),
        switchMap(input => {
          this.isLoading = true;

          return this.fetchFn(
            input.pageNumber ?? 1,
            this.pageSize,
            input.term ?? ''
          ).pipe(
            map(res => res?.data ?? res),
            tap(data => {
              this.isLoading = false;

              if (input.pageNumber === 1) {
                // الصفحة الأولى → استبدال القائمة
                let filtered = data.filter(
                  (el: any) =>
                    !this.filteroutIds.includes(
                      el[this.fileroutKey]?.toString() ?? ''
                    )
                );

                // Preserve selected row(s) if they are not in the new page result
                const currentValue = this.control?.value;
                if (
                  currentValue != null &&
                  this.mergeFetchedSelectedIntoItems
                ) {
                  const currentItems = this.items$.getValue();
                  if (this.multiple && Array.isArray(currentValue)) {
                    for (const id of currentValue) {
                      const selectedItem = currentItems.find(
                        (item) => item[this.bindValue] === id
                      );
                      if (
                        selectedItem &&
                        !filtered.some(
                          (item: any) => item[this.bindValue] === id
                        )
                      ) {
                        filtered = [selectedItem, ...filtered];
                      }
                    }
                  } else if (!Array.isArray(currentValue)) {
                    const selectedItem = currentItems.find(
                      (item) => item[this.bindValue] === currentValue
                    );
                    if (selectedItem) {
                      const existsInNewData = filtered.some(
                        (item: any) =>
                          item[this.bindValue] === currentValue
                      );
                      if (!existsInNewData) {
                        filtered = [selectedItem, ...filtered];
                      }
                    }
                  }
                }

                this.items$.next(filtered);
              } else {
                // pagination → دمج النتائج مع منع التكرار
                const currentItems = this.items$.getValue();
                const currentValues = new Set(currentItems.map(item => item[this.bindValue]));
                
                // Filter out items that already exist (by bindValue) and filteroutIds
                const newItems = data.filter(
                  (el: any) => {
                    const value = el[this.bindValue];
                    const isFilteredOut = this.filteroutIds.includes(
                      el[this.fileroutKey]?.toString() ?? ''
                    );
                    return !currentValues.has(value) && !isFilteredOut;
                  }
                );

                const merged = [...currentItems, ...newItems];
                this.items$.next(merged);
              }

              this.hasMoreItems = data?.length >= this.pageSize;

              // التحقق من أن القيمة المختارة ما زالت موجودة
              queueMicrotask(() => {
                this.validateCurrentValueAgainstItems();
              });
            })
          );
        })
      )
      .subscribe({
        next: () => {
          // في حال كانت القيمة موجودة ولكن لم تُجلب بعد
          if (
            this.control?.value !== null &&
            this.control?.value !== undefined &&
            !this.itemFetched
          ) {
            this.writeValue(this.control.value);
          }
        },
        error: err => {
          console.error(err);
          this.isLoading = false;
        }
      });
  }

  initializeData() {
    this.onSearch('', 1);
    this.getLabel();
  }

  onOpen() {
    // If we only have 1 or 0 items, it might be that we only fetched the selected item.
    // Refresh the list to show all options.
    const currentItems = this.items$.getValue();
    if (currentItems.length <= 1) {
      this.onSearch('', 1);
    }
  }


  compareFn = (a: any, b: any) => a[this.bindValue] === b[this.bindValue];


  onSearch(term: string, pageNumber = 1): void {
    this.currentPage = pageNumber;

    if (pageNumber === 1) {
      // Preserve selected row(s) while the list reloads (ng-select multi uses number[]).
      const currentValue = this.control?.value;
      const currentItems = this.items$.getValue();
      let preserved: any[] = [];
      if (this.multiple && Array.isArray(currentValue)) {
        preserved = currentItems.filter((item) =>
          currentValue.includes(item[this.bindValue])
        );
      } else if (currentValue != null && !Array.isArray(currentValue)) {
        const one = currentItems.find(
          (item) => item[this.bindValue] === currentValue
        );
        preserved = one ? [one] : [];
      }
      this.items$.next(preserved);
      this.hasMoreItems = true;
    }

    this.searchTerm$.next({ term, pageNumber });

    if (this.clearAfterSelection) this.clearGurad = true;
  }

  onLoadMore(): void {
    if (this.hasMoreItems && !this.isLoading) {
      this.currentPage++;
      this.onSearch(this.searchTerm$.getValue().term, this.currentPage);
    }
  }

  writeValue(value: any): void {
    this.value = value;

    if (value != null && value !== undefined) {
      const currentItems = this.items$.getValue();

      // Multi-select: value is number[] — never pass the array to fetchSelectedItem (report lookups use page/term signature).
      if (this.multiple && Array.isArray(value)) {
        if (value.length === 0) {
          this.itemFetched = false;
          return;
        }
        const missingIds = value.filter(
          (id: any) =>
            !currentItems.some((item) => item[this.bindValue] === id)
        );
        if (missingIds.length === 0) {
          this.itemFetched = true;
          return;
        }
        this.isLoading = true;
        this.fetchFn(1, this.pageSize, '')
          .pipe(
            map((res) => res?.data ?? res),
            takeUntil(this.destory$)
          )
          .subscribe({
            next: (data: any[]) => {
              this.isLoading = false;
              const rows = data ?? [];
              const picked = rows.filter((el: any) =>
                missingIds.includes(el[this.bindValue])
              );
              let merged = [...this.items$.getValue()];
              for (const row of picked) {
                if (
                  !merged.some((m) => m[this.bindValue] === row[this.bindValue])
                ) {
                  merged.push(row);
                }
              }
              this.items$.next(merged);
              this.itemFetched = true;
            },
            error: () => {
              this.isLoading = false;
            },
          });
        return;
      }

      const found = currentItems.find(
        (item) => item[this.bindValue] === value
      );

      if (!found && this.fetchSelectedItem) {
        // Only fetch if we haven't already fetched this item or if itemFetched flag is false
        if (
          !this.itemFetched ||
          !currentItems.some((item) => item[this.bindValue] === value)
        ) {
          this.isLoading = true;

          this.fetchSelectedItem(value)
            .pipe(takeUntil(this.destory$))
            .subscribe(
              (item) => {
                this.isLoading = false;

                const daItem = item?.data ? item?.data : item;
                if (daItem) {
                  if (this.mergeFetchedSelectedIntoItems) {
                    // Remove any existing item with same bindValue as daItem to avoid duplicates
                    const list = this.items$.getValue().filter(
                      (el) => el[this.bindValue] !== daItem[this.bindValue]
                    );
                    const updatedItems = [...list, daItem];

                    this.items$.next(updatedItems);

                    // When fetched item id differs from current value (e.g. 0 → default id), update form so display matches
                    if (
                      daItem[this.bindValue] != null &&
                      daItem[this.bindValue] !== value
                    ) {
                      this.value = daItem[this.bindValue];
                      this.onChange(daItem[this.bindValue]);
                      this.control?.setValue(daItem[this.bindValue], {
                        emitEvent: false,
                      });
                    }
                  }

                  this.itemFetched = true;
                }
              },
              (error) => {
                this.isLoading = false;
                console.error('Error fetching selected item:', error);
              }
            );
        }
      } else if (found) {
        // Item already exists in the list, mark as fetched
        this.itemFetched = true;
      }
    } else {
      // Value is null/undefined, reset the fetched flag
      this.itemFetched = false;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onValueChange(value: any): void {
    this.value = value;
    this.onChange(value);
    this.control?.setValue(value, { emitEvent: false });

    if (this.multiple && Array.isArray(value)) {
      const items = this.items$.getValue();
      const selected = items.filter((item) =>
        value.includes(item[this.bindValue])
      );
      this.emitSelectOption.emit(selected);
    } else {
      const selectedItem = this.items$
        .getValue()
        .find((item) => item[this.bindValue] === value);
      if (this.customSearch) {
        this.emitSelectOption.emit({
          data: selectedItem,
          customSearch: this.customSearch,
        });
      } else {
        this.emitSelectOption.emit(selectedItem);
      }
    }

    if (this.searchTerm$.getValue().term !== '') {
      this.onSearch('');
    }

    this.customSearch = undefined;

    if (this.clearAfterSelection && this.clearGurad) {
      this.clearGurad = false;
      setTimeout(() => {
        this.selectRef.handleClearClick();
      }, 0);
    }
  }

  isInvalid(): boolean {
    const control = this.control;
    return control ? control.invalid && (control.dirty || control.touched) : false;
  }

  ngOnDestroy(): void {
    this.destory$.next(undefined);
    this.destory$.complete();
  }

  private validateCurrentValueAgainstItems() {
    if (!this.control) return;

    const value = this.control.value;
    if (value == null) return;

    if (this.multiple && Array.isArray(value)) {
      return;
    }

    if (!this.mergeFetchedSelectedIntoItems) {
      return;
    }

    // لا تُمسح القيمة إذا كنّا بانتظار fetchSelectedItem — القيمة صحيحة لكنّها لم تُضف بعد إلى items
    if (!this.itemFetched) return;

    const exists = this.items$
      .getValue()
      .some(item => item[this.bindValue] === value);

    if (!exists) {
      this.itemFetched = false;
      this.value = null;

      this.control.setValue(null, { emitEvent: false });
      this.onChange(null);

      // لو تستخدم ng-select
      if (this.selectRef) {
        this.selectRef.clearModel();
      }
    }
  }

}
